import { defaultTravellers, TrackingInputFieldName } from 'constants/TravelConstants';
import { SearchWidgetActions } from 'actions/TravelActions/SearchWidgetActions';
import { TripType as TripTypeEnum } from '@rome/response/page/v4/travel/flights/v1/enums/TripType';
import { PAGE_DATA_ACTIONS } from 'actions/pageActionsV4';
import { SlotType } from '@rome/response/page/v4/SlotType';
import { WidgetTypeV4 } from '@rome/response/page/v4/WidgetTypeV4';
import get from 'lodash/get';
import { TravelType } from '@rome/response/page/v4/travel/TravelType';
import { CabinClass as CabinClassEnum } from '@rome/response/page/v4/travel/flights/v1/enums/CabinClass';
import { getDateObjectFromString, getDateStringFromObject } from 'utils/timeUtils';
import set_date from 'date-fns/set_date';
//24 (hours in day) * 60(min in hours) * 60(seconds in min) * 1000(ms in seconds);
const oneDayinMS = 86400000;
export const getFutureDate = (date, incrementDays = 2) => {
    const futureDayMS = incrementDays * oneDayinMS;
    const endDate = new Date(date.getTime() + futureDayMS);
    return endDate;
};
export const getJourneyDate = () => {
    let today = new Date(Date.now());
    const currentHour = today.getHours();
    /** if time is greater then 11:00 am (11:00:00) then change today to next day. */
    if (currentHour >= 11) {
        today = set_date(today, today.getDate() + 1);
    }
    return today;
};
const todayDate = new Date(Date.now());
const journeyDate = getJourneyDate();
// creates key for every journey by concatenating journey data.
const createKeyForJourney = (journeyData) => {
    let keyArray = [];
    const { origin, dest, journeyStartDate, journeyEndDate } = journeyData;
    if (origin && origin.value) {
        keyArray.push(origin.value.airportCode);
    }
    if (dest && dest.value) {
        keyArray.push(dest.value.airportCode);
    }
    if (journeyStartDate) {
        keyArray.push(getDateStringFromObject(journeyStartDate));
    }
    if (journeyEndDate) {
        keyArray.push(getDateStringFromObject(journeyEndDate));
    }
    return keyArray.join('-');
};
const defaultJourneyData = {
    minDate: todayDate,
    journeyStartDate: journeyDate,
    requestKey: journeyDate.toString(),
    origin: { isDirty: false, isEmpty: true },
    dest: { isDirty: false, isEmpty: true }
};
// convert DDMMYYYY to YYYYMMDD, which is further used to compare two date strings on the basis of date, year and month.
const reverseDateString = (dateString) => {
    return `${dateString.slice(4)}${dateString.slice(2, 4)}${dateString.slice(0, 2)}`;
};
/**
 * compares two date strings and returns true if date1 is greater than or equal to date2
 * This is done to compare two dates on basis of date, month and year only, and exclude the comparisons of hours, seconds, ms.
 * the two dates are first converted to YYYYMMDD format for better comparison.
 *
 * Example: Let's say,
 * date1 = 24th Apr 2020
 * date2 = 12th May 2020
 *
 * To compare these two dates, first they'll be converted into YYYYMMDD format, so that it's easy to compare these integers.
 * After conversion:
 * date1 = 20200424
 * date2 = 20200512
 *
 * Comparing these two (numerically), we can infer that date2 > date1.
 **/
const compareDates = (date1, date2) => {
    return (Number(reverseDateString(getDateStringFromObject(date1))) >=
        Number(reverseDateString(getDateStringFromObject(date2))));
};
const getTravelSearchWidgetData = slots => {
    return (slots &&
        Array.isArray(slots) &&
        slots.find(slot => slot.slotType === SlotType.WIDGET && get(slot, 'widget.type') === WidgetTypeV4.TRAVEL_SEARCH_V1));
};
const getFlightSearchDataFromResponse = (pageResponse) => {
    const widget = getTravelSearchWidgetData(pageResponse.slots);
    if (!widget) {
        return undefined;
    }
    const data = get(widget, ['widget', 'data']);
    const FlightSearchData = data.travelOptions.find(travelOption => travelOption.travelType === TravelType.FLIGHT);
    return FlightSearchData;
};
/**
 * This function takes page response and pre-fills the search data for flights.
 * Default values:
 * 		tripType:  ONE_WAY
 * 		departDate: today
 *
 * If `prefilledData` gives tripType as ROUND_TRIP then prefill `returnDate` with 2 days in future from today.
 */
const getPrefilledFlightData = (FlightSearchData) => {
    const prefilledData = (FlightSearchData && FlightSearchData.preFilledData) || undefined;
    const cabinClasses = FlightSearchData && FlightSearchData.cabinClasses;
    const defaultCabinClass = cabinClasses &&
        cabinClasses.find(cabinItem => cabinItem.cabinClass === CabinClassEnum.ECONOMY).paramValue;
    if (prefilledData) {
        const journeyList = [];
        prefilledData.trips &&
            prefilledData.trips.forEach((trip, index) => {
                const preFilledStartDate = trip.startDate && getDateObjectFromString(trip.startDate);
                const preFilledEndDate = trip.returnDate && getDateObjectFromString(trip.returnDate);
                // compareDates fn return true if prefilledStartDate is greater than or equal to todayDate.
                const journeyStartDate = preFilledStartDate && compareDates(preFilledStartDate, todayDate) ? preFilledStartDate : journeyDate;
                // Sanity Check: in case of prefilled data, end date should not be less than startDate.
                const journeyEndDate = preFilledEndDate && compareDates(preFilledEndDate, journeyStartDate)
                    ? preFilledEndDate
                    : prefilledData.tripType && prefilledData.tripType.tripType === TripTypeEnum.ROUND_TRIP
                        ? getFutureDate(journeyStartDate)
                        : undefined;
                const minDateFromString = index > 0 && trip[index - 1].startDate && getDateObjectFromString(trip[index - 1].startDate);
                const minDate = minDateFromString || todayDate;
                const newJourney = {
                    minDate,
                    journeyStartDate,
                    journeyEndDate,
                    origin: Object.assign(Object.assign({}, defaultJourneyData.origin), (trip.from && { value: trip.from, isEmpty: false })),
                    dest: Object.assign(Object.assign({}, defaultJourneyData.origin), (trip.to && { value: trip.to, isEmpty: false }))
                };
                newJourney.requestKey = createKeyForJourney(newJourney);
                journeyList.push(newJourney);
            });
        const preFilledSearchData = {
            selectedTripType: get(prefilledData, 'tripType.tripType') || TripTypeEnum.ONE_WAY,
            journeyList,
            cabinClass: get(prefilledData, 'cabinClass.paramValue') || defaultCabinClass,
            travellers: prefilledData.travellers || defaultTravellers,
            availableClasses: cabinClasses
        };
        return preFilledSearchData;
    }
    else {
        return {
            selectedTripType: TripTypeEnum.ONE_WAY,
            cabinClass: defaultCabinClass,
            travellers: defaultTravellers,
            journeyList: [defaultJourneyData],
            availableClasses: cabinClasses
        };
    }
};
const defaultState = {
    journeyList: [defaultJourneyData],
    selectedTripType: TripTypeEnum.ONE_WAY,
    travellers: defaultTravellers,
    fieldChanged: TrackingInputFieldName.EMPTY
};
export default function SearchWidgetData(state = defaultState, action) {
    switch (action.type) {
        case PAGE_DATA_ACTIONS.SUCCESS:
            const pageResponse = action.data;
            if (!pageResponse) {
                return state;
            }
            const FlightSearchData = getFlightSearchDataFromResponse(pageResponse);
            const prefilledData = getPrefilledFlightData(FlightSearchData);
            return Object.assign({}, defaultState, prefilledData, { initialPrefillData: prefilledData });
        case SearchWidgetActions.SET_MODIFY_SEARCH_DATA:
            const pageData = action.data;
            if (!pageData) {
                return state;
            }
            const modifyData = getPrefilledFlightData(pageData);
            return Object.assign({}, defaultState, modifyData);
        case SearchWidgetActions.SET_DATA:
            return Object.assign({}, state, action.data);
        case SearchWidgetActions.ADD_JOURNEY:
            const journeyList = state.journeyList.map((journey, index) => {
                if (index !== action.index) {
                    return journey;
                }
                const updatedJourney = Object.assign(Object.assign({}, journey), action.data);
                return Object.assign(Object.assign({}, updatedJourney), { requestKey: createKeyForJourney(updatedJourney) });
            });
            let selectedTripType = state.selectedTripType !== TripTypeEnum.MULTI_CITY &&
                state.journeyList.length === 1 &&
                action.data.journeyEndDate
                ? TripTypeEnum.ROUND_TRIP
                : state.selectedTripType;
            return Object.assign(Object.assign({}, state), { selectedTripType,
                journeyList });
        case SearchWidgetActions.CHANGE_TRIP_TYPE: {
            const newState = Object.assign({}, state);
            newState.selectedTripType = action.tripType;
            // set or unset end date corresponding to trip type
            if (action.tripType === TripTypeEnum.ONE_WAY) {
                newState.journeyList[0].journeyEndDate = undefined;
                newState.fieldChanged = TrackingInputFieldName.TRIP_TYPE;
            }
            else if (action.tripType === TripTypeEnum.ROUND_TRIP) {
                const startDate = new Date(newState.journeyList[0].journeyStartDate);
                const endDate = getFutureDate(startDate);
                newState.journeyList[0].journeyEndDate = endDate;
                newState.fieldChanged = TrackingInputFieldName.TRIP_TYPE_RETURN_DATE;
            }
            return newState;
        }
        case SearchWidgetActions.SET_FIELD_CHANGED:
            return Object.assign({}, state, { fieldChanged: action.data });
        default:
            return state;
    }
}
